// THE NEXT SEASON on the League page once a season is over
// (PLAN-2026-10-27-the-next-season §5, scenario H6): a commissioner starts it
// from here, everyone else is told who will, and once it is started every
// season's page leads to it.
//
// THE FORMAT IS ASKED AS IT STARTS, last season's chosen, and the sheet says
// before the button that it is then fixed for the season (CLAUDE.md: say what
// will happen when you cannot make it pleasant). The rest of the rules stay
// the commissioner's to change until the draft, in League settings.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { valueOf, type HomeView, type SeasonFormat } from '@fsp/domain';
import { messageOf } from '../../data/api';
import { useLeagues } from '../../data/LeaguesProvider';
import { startNextSeason } from '../../data/nextSeason';
import { useLeagueRules } from '../../data/rules';
import { useDelight } from '../../delight/useDelight';
import { Panel } from '../../kit/Panel';
import { Sheet } from '../../kit/overlays';
import { Button, Callout, NumberField, OneOf, Stack, Text, tell } from '../../kit/ui';

const FORMATS: readonly { value: SeasonFormat; label: string }[] = [
  { value: 'redraft', label: 'Redraft' },
  { value: 'keeper', label: 'Keepers' },
  { value: 'dynasty', label: 'Dynasty' },
];

/**
 * What the format does, in a sentence. A dynasty's draft is the one its rules
 * give it (PLAN-2026-10-27-the-next-season §2.4 rule 2): a league that becomes
 * one gets the rookie draft, and a dynasty already keeps its own.
 */
function formatLine(
  format: SeasonFormat,
  kept: number,
  last: {
    mode: SeasonFormat;
    rookieDraft: boolean;
    /** Last season was itself started from the one before, its rosters carried. */
    carried: boolean;
    auction: boolean;
  },
): string {
  switch (format) {
    case 'redraft':
      return 'Everyone starts fresh: every player back in the pool and a new draft.';
    case 'keeper':
      return `Each team keeps up to ${kept === 1 ? 'one player' : `${kept} players`} from last season's roster, chosen before the draft. The rules set what each one costs.`;
    case 'dynasty':
      return !(last.mode === 'dynasty' && last.carried)
        ? last.auction
          ? 'Every team keeps its whole roster, and a short auction of the new rookies follows, four players a team. A team that ends up over its roster’s size lets players go before it can add one.'
          : 'Every team keeps its whole roster, and a short draft of the new rookies follows: four rounds, last place picking first. A team that ends up over its roster’s size lets players go before it can add one.'
        : last.rookieDraft
          ? 'Every team keeps its whole roster, and the draft takes the new rookies.'
          : 'Every team keeps its whole roster, and the draft takes any player on no team.';
  }
}

export function FrontNextSeason({ home }: { home: HomeView }) {
  const { league } = home;
  const [open, setOpen] = useState(false);
  const next = league.season + 1;
  return (
    <div className="fsp-front-section">
      <Panel title="Next season">
        <div className="fsp-front-wrap">
          {league.nextSeasonId !== null ? (
            <>
              <Text>The {next} season is set up.</Text>
              <div>
                <Button
                  variant="primary"
                  size="sm"
                  iconRight="next"
                  to={`/l/${league.nextSeasonId}`}
                >
                  Go to the {next} season
                </Button>
              </div>
            </>
          ) : league.isCommissioner ? (
            <>
              <Text>
                The {league.season} season is over. When you are ready, start the next one: everyone
                in the league comes along.
              </Text>
              <div>
                <Button variant="primary" size="sm" icon="play" onClick={() => setOpen(true)}>
                  Start the {next} season
                </Button>
              </div>
            </>
          ) : (
            <Text>
              The {league.season} season is over. {league.commissionerName} starts the next one, and
              you will be told when it is set up.
            </Text>
          )}
        </div>
      </Panel>
      {league.isCommissioner && league.nextSeasonId === null ? (
        <Sheet opened={open} onClose={() => setOpen(false)} title={`Start the ${next} season`}>
          {open ? <StartSheet home={home} onDone={() => setOpen(false)} /> : null}
        </Sheet>
      ) : null}
    </div>
  );
}

function StartSheet({ home, onDone }: { home: HomeView; onDone: () => void }) {
  const { league } = home;
  const rules = valueOf(useLeagueRules());
  const leagues = useLeagues();
  const navigate = useNavigate();
  const { play } = useDelight();
  const [format, setFormat] = useState<SeasonFormat | null>(null);
  const [kept, setKept] = useState<number | null>(null);
  const [starting, setStarting] = useState(false);
  if (rules === null) return <Text tone="secondary">Reading the rules…</Text>;

  const last = rules.settings.keepers;
  const chosen = format ?? last.mode;
  const count = kept ?? (last.keeperCount > 0 ? last.keeperCount : 2);
  const next = league.season + 1;
  const start = async () => {
    setStarting(true);
    const reply = await startNextSeason(league.id, {
      format: chosen,
      ...(chosen === 'keeper' ? { keeperCount: count } : {}),
    });
    setStarting(false);
    if (reply.status !== 'ok') {
      tell(
        messageOf(
          reply,
          `That did not work. The ${next} season was not started.`,
          `Could not reach the site. The ${next} season was not started.`,
        ),
        'bad',
      );
      return;
    }
    // The hub's card for this league is now the new season's.
    leagues.reload();
    play('win');
    tell(`The ${next} season is set up. Everyone has been told.`, 'good');
    onDone();
    navigate(`/l/${reply.data.id}`);
  };

  return (
    <Stack gap={4}>
      <Text>
        Everyone in {league.name} comes along: every manager and their team, its name and colors,
        and every bot. Anyone who does not want another season can leave it, and before the draft
        you can open their seat from the league's page.
      </Text>
      <OneOf label="How the league goes on" options={FORMATS} value={chosen} onChange={setFormat} />
      <Text tone="secondary">
        {formatLine(chosen, count, {
          ...last,
          carried: rules.league.lastSeasonId !== null,
          auction: rules.settings.draft.format === 'auction',
        })}
      </Text>
      {chosen === 'keeper' ? (
        <NumberField
          label="Players each team keeps"
          value={count}
          onChange={(v) => setKept(v)}
          min={1}
          max={rules.settings.draft.rounds}
        />
      ) : null}
      <Callout>
        The format is set for the {next} season once you start it. The rest of the rules stay yours
        to change until the draft.
      </Callout>
      <div>
        <Button variant="primary" loading={starting} onClick={() => void start()}>
          Start the {next} season
        </Button>
      </div>
    </Stack>
  );
}
